import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Modal, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '../../../i18n';
import { mergeCollaborations } from '../../../store/request';
import type { TargetCollaboration } from '../../../types/collaboration';

type MergeInviteGroupModalProps = {
  open: boolean;
  shopId?: string | null;
  invitations: TargetCollaboration[];
  onClose: () => void;
  onMerged?: () => void;
};

function localId(row: TargetCollaboration) {
  if (row.id == null) return null;
  const id = typeof row.id === 'number' ? row.id : Number(row.id);
  return Number.isFinite(id) && Number.isInteger(id) ? id : null;
}

export function MergeInviteGroupModal({ open, shopId, invitations, onClose, onMerged }: MergeInviteGroupModalProps) {
  const { t } = useTranslation();
  const currentShopId = shopId?.trim() || '';
  // 列表页已筛过可合并项，这里只保留有合法 id 的
  const candidates = useMemo(
    () => invitations.filter((row) => localId(row) != null),
    [invitations]
  );
  const primaryOptions = useMemo(
    () =>
      candidates.map((row) => ({
        value: localId(row) as number,
        label: row.name?.trim() || String(localId(row))
      })),
    [candidates]
  );
  const [primaryId, setPrimaryId] = useState<number | undefined>();
  const [deadline, setDeadline] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const first = candidates[0];
    setPrimaryId(localId(first) ?? undefined);
    const parsed = first?.endTime ? dayjs(first.endTime) : null;
    setDeadline(parsed?.isValid() && parsed.endOf('day').isAfter(dayjs()) ? parsed : null);
    setSubmitting(false);
  }, [open, candidates]);

  const submit = async () => {
    if (submitting) return;
    if (!currentShopId || currentShopId.length > 64) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const selectedCollaborationIds = candidates
      .map((row) => localId(row))
      .filter((id): id is number => id != null);
    if (selectedCollaborationIds.length < 2) {
      message.warning(t('invitation.pick_two'));
      return;
    }
    if (selectedCollaborationIds.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return;
    }
    if (primaryId == null || !selectedCollaborationIds.includes(primaryId)) {
      message.warning(t('invitation.pick_primary'));
      return;
    }
    if (!deadline) {
      message.warning(t('invitation.pick_deadline'));
      return;
    }
    const end = deadline.endOf('day');
    if (!end.isAfter(dayjs())) {
      message.warning(t('invitation.merge_end_invalid'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await mergeCollaborations({
        shopId: currentShopId,
        primaryCollaborationId: primaryId,
        selectedCollaborationIds,
        endTime: end.toISOString()
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.merge_failed'));
        return;
      }
      const result = res.content?.result;
      const detail = res.content?.message || res.message;
      if (result === 'FAILED' || result === 'UNKNOWN') {
        message.warning(detail || t('invitation.merge_failed'));
        return;
      }
      if (result === 'PARTIAL_SUCCESS') {
        message.warning(detail || t('invitation.merge_success'));
      } else {
        message.success(detail || t('invitation.merge_success'));
      }
      onMerged?.();
      onClose();
    } catch {
      message.error(t('invitation.merge_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const tooFewOngoing = open && invitations.length >= 2 && candidates.length < 2;

  return (
    <Modal
      title={t('invitation.merge_groups')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={480}
      closeIcon={
        <span className='cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700'>{t('common.close')}</span>
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type='primary'
            className='wb-btn-primary rounded-lg'
            loading={submitting}
            disabled={primaryId == null || !deadline || candidates.length < 2}
            onClick={submit}>
            {t('invitation.merge')}
          </Button>
        </div>
      }>
      <p className='mb-2 mt-0 text-xs leading-relaxed text-slate-400'>{t('invitation.merge_hint')}</p>
      {tooFewOngoing ? (
        <p className='mb-0 text-sm text-amber-600'>{t('invitation.too_few')}</p>
      ) : (
        <div className='space-y-4'>
          <div>
            <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.primary')}</div>
            <Select
              className='w-full'
              placeholder={t('invitation.primary_placeholder')}
              value={primaryId}
              options={primaryOptions}
              onChange={setPrimaryId}
            />
          </div>
          <div>
            <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.deadline_after')}</div>
            <DatePicker
              className='w-full'
              value={deadline}
              format='YYYY/MM/DD'
              disabledDate={(current) => Boolean(current && current.endOf('day').isBefore(dayjs()))}
              onChange={setDeadline}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
